import { buildBoundaries, distanceToPolyline, smoothChain } from './boundary';
import {
  clonePaintTree, nodeAtPath, resolvedState, stateForExtruder,
} from './paintCodec';
import {
  add, angleDegrees, centroid, distance, dot, length, normalize, scale, sub,
} from './geometry';
import { rebuildPartLeaves } from './parser';
import type {
  BoundaryChain, EditCommand, EditorDocument, PaintNode, SmoothOptions,
  SourcePart, TreeChange, Vec3,
} from './types';

export function bucketFill(
  document: EditorDocument,
  partIndex: number,
  seedLeafId: number,
  targetExtruder: number,
  slopeLimit: number,
): EditCommand | null {
  const part = document.parts[partIndex];
  const seed = part?.leaves[seedLeafId];
  if (!part || !seed || seed.extruder === targetExtruder) return null;
  const sourceExtruder = seed.extruder;
  const queue = [seedLeafId];
  const visited = new Uint8Array(part.leaves.length);
  const changes: NonNullable<EditCommand['paintChanges']> = [];
  const affectedTriangles = new Set<number>();
  while (queue.length) {
    const id = queue.pop()!;
    if (visited[id]) continue;
    visited[id] = 1;
    const leaf = part.leaves[id];
    if (leaf.extruder !== sourceExtruder) continue;
    changes.push({ partIndex, leafId: id, oldExtruder: leaf.extruder, newExtruder: targetExtruder });
    affectedTriangles.add(leaf.sourceTriangle);
    for (const neighborId of leaf.neighbors) {
      const neighbor = part.leaves[neighborId];
      if (!visited[neighborId] && neighbor.extruder === sourceExtruder && angleDegrees(leaf.normal, neighbor.normal) <= slopeLimit) queue.push(neighborId);
    }
  }
  if (!changes.length) return null;
  for (const change of changes) {
    const leaf = part.leaves[change.leafId];
    leaf.extruder = change.newExtruder;
    const node = nodeAtPath(part.triangles[leaf.sourceTriangle].tree, leaf.path);
    if (node) node.state = stateForExtruder(change.newExtruder, part.defaultExtruder);
  }
  const baseChanges = [...changes];
  for (let siblingIndex = 0; siblingIndex < document.parts.length; siblingIndex += 1) {
    const sibling = document.parts[siblingIndex];
    if (siblingIndex === partIndex || sibling.key !== part.key) continue;
    for (const baseChange of baseChanges) {
      const baseLeaf = part.leaves[baseChange.leafId];
      const siblingLeaf = sibling.leaves.find((leaf) => leaf.sourceTriangle === baseLeaf.sourceTriangle && samePath(leaf.path, baseLeaf.path));
      if (!siblingLeaf) continue;
      const baseNode = nodeAtPath(part.triangles[baseLeaf.sourceTriangle].tree, baseLeaf.path);
      const siblingNode = nodeAtPath(sibling.triangles[siblingLeaf.sourceTriangle].tree, siblingLeaf.path);
      if (!baseNode || !siblingNode) continue;
      const oldExtruder = siblingLeaf.extruder;
      siblingNode.state = baseNode.state;
      siblingLeaf.extruder = resolvedState(baseNode.state, sibling.defaultExtruder);
      changes.push({ partIndex: siblingIndex, leafId: siblingLeaf.id, oldExtruder, newExtruder: siblingLeaf.extruder });
    }
  }
  refreshBoundaries(document);
  document.dirty = true;
  return { label: `Bucket fill (${changes.length.toLocaleString()} faces)`, paintChanges: changes };
}

export function previewSmoothedBoundary(document: EditorDocument, chainId: string, options: SmoothOptions): BoundaryChain | null {
  const chain = document.boundaries.find((candidate) => candidate.id === chainId);
  if (!chain) return null;
  return smoothChain(chain, document.boundarySegments, document.parts[chain.partIndex], options.strength,
    options.maxMovement, options.tolerance, options.creaseAngle);
}

export function applySmoothing(document: EditorDocument, chainIds: string[], options: SmoothOptions): EditCommand | null {
  const changes = new Map<string, TreeChange>();
  const processedDefinitions = new Set<string>();
  for (const requestedId of chainIds) {
    const chain = document.boundaries.find((candidate) => candidate.id === requestedId);
    if (!chain) continue;
    const definitionKey = `${document.parts[chain.partIndex].key}:${chain.colorA}:${chain.colorB}:${[...chain.sourceTriangles].sort((a, b) => a - b).join(',')}:${chain.originalPoints.length}`;
    if (processedDefinitions.has(definitionKey)) continue;
    processedDefinitions.add(definitionKey);
    const smoothed = smoothChain(chain, document.boundarySegments, document.parts[chain.partIndex], options.strength,
      options.maxMovement, options.tolerance, options.creaseAngle);
    applySmoothedChain(document, chain, smoothed, options, changes);
    syncSharedTrees(document, chain.partIndex, changes);
  }
  if (!changes.size) return null;
  refreshBoundaries(document);
  document.dirty = true;
  return { label: chainIds.length === 1 ? 'Smooth boundary' : 'Smooth all boundaries', treeChanges: [...changes.values()] };
}

function syncSharedTrees(document: EditorDocument, sourcePartIndex: number, changes: Map<string, TreeChange>): void {
  const sourcePart = document.parts[sourcePartIndex];
  const sourceChanges = [...changes.values()].filter((change) => change.partIndex === sourcePartIndex);
  const rebuilt = new Set<number>([sourcePartIndex]);
  for (let partIndex = 0; partIndex < document.parts.length; partIndex += 1) {
    const part = document.parts[partIndex];
    if (partIndex === sourcePartIndex || part.key !== sourcePart.key) continue;
    for (const sourceChange of sourceChanges) {
      const triangle = part.triangles[sourceChange.triangleIndex];
      if (!triangle) continue;
      const key = `${partIndex}:${sourceChange.triangleIndex}`;
      const prior = changes.get(key);
      const oldTree = prior?.oldTree ?? clonePaintTree(triangle.tree);
      triangle.tree = clonePaintTree(sourceChange.newTree);
      changes.set(key, { partIndex, triangleIndex: sourceChange.triangleIndex, oldTree, newTree: clonePaintTree(triangle.tree) });
      rebuilt.add(partIndex);
    }
  }
  for (const partIndex of rebuilt) refreshPart(document, partIndex);
}

function samePath(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function applySmoothedChain(
  document: EditorDocument,
  original: BoundaryChain,
  target: BoundaryChain,
  options: SmoothOptions,
  changes: Map<string, TreeChange>,
): void {
  const part = document.parts[original.partIndex];
  const candidates = affectedSourceTriangles(original, part);
  const originalLeaves = part.leaves;
  const band = options.maxMovement + 3 * options.tolerance;
  for (const triangleIndex of candidates) {
    const triangle = part.triangles[triangleIndex];
    if (!triangle) continue;
    const corners = triangle.vertices.map((index) => part.vertices[index]) as [Vec3, Vec3, Vec3];
    if (!triangleNearBoundary(corners, target.smoothedPoints, target.closed, band)) continue;
    const oldTree = clonePaintTree(triangle.tree);
    const classifier = (point: Vec3): number => classifyTarget(point, original, target, document.boundarySegments, originalLeaves, triangleIndex, band);
    const newTree = adaptiveTree(corners, classifier, target, options.tolerance, part.defaultExtruder, 0);
    triangle.tree = newTree;
    const key = `${original.partIndex}:${triangleIndex}`;
    const prior = changes.get(key);
    changes.set(key, { partIndex: original.partIndex, triangleIndex, oldTree: prior?.oldTree ?? oldTree, newTree: clonePaintTree(newTree) });
  }
}

function affectedSourceTriangles(chain: BoundaryChain, part: SourcePart): Set<number> {
  const result = new Set(chain.sourceTriangles);
  for (const leaf of part.leaves) {
    if (!result.has(leaf.sourceTriangle)) continue;
    for (const neighbor of leaf.neighbors) result.add(part.leaves[neighbor].sourceTriangle);
  }
  return result;
}

function triangleNearBoundary(corners: [Vec3, Vec3, Vec3], points: Vec3[], closed: boolean, band: number): boolean {
  const samples = [...corners, centroid(...corners)];
  if (samples.some((point) => distanceToPolyline(point, points, closed).distance <= band)) return true;
  const center = centroid(...corners);
  const radius = Math.max(...corners.map((point) => distance(point, center)));
  return distanceToPolyline(center, points, closed).distance <= radius + band;
}

function classifyTarget(
  point: Vec3,
  original: BoundaryChain,
  target: BoundaryChain,
  segments: EditorDocument['boundarySegments'],
  leaves: SourcePart['leaves'],
  sourceTriangle: number,
  band: number,
): number {
  const nearestTarget = distanceToPolyline(point, target.smoothedPoints, target.closed);
  if (nearestTarget.distance > band) return originalColorAtPoint(point, leaves, sourceTriangle);
  let nearestOriginalSegment = segments[original.segmentIndices[0]];
  let best = Number.POSITIVE_INFINITY;
  for (const segmentIndex of original.segmentIndices) {
    const segment = segments[segmentIndex];
    const d = distanceToPolyline(nearestTarget.point, [segment.a, segment.b], false).distance;
    if (d < best) { best = d; nearestOriginalSegment = segment; }
  }
  if (!nearestOriginalSegment) return originalColorAtPoint(point, leaves, sourceTriangle);
  const side = dot(sub(point, nearestTarget.point), nearestOriginalSegment.lateralToB);
  if (Math.abs(side) < 1e-8) return originalColorAtPoint(point, leaves, sourceTriangle);
  return side >= 0 ? original.colorB : original.colorA;
}

function originalColorAtPoint(point: Vec3, leaves: SourcePart['leaves'], sourceTriangle: number): number {
  const candidates = leaves.filter((leaf) => leaf.sourceTriangle === sourceTriangle);
  let bestColor = candidates[0]?.extruder ?? 1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const leaf of candidates) {
    const center = centroid(...leaf.vertices);
    const d = distance(point, center);
    if (d < bestDistance) { bestDistance = d; bestColor = leaf.extruder; }
  }
  return bestColor;
}

function adaptiveTree(
  corners: [Vec3, Vec3, Vec3],
  classify: (point: Vec3) => number,
  chain: BoundaryChain,
  tolerance: number,
  inheritedExtruder: number,
  depth: number,
): PaintNode {
  const center = centroid(...corners);
  const mids: Vec3[] = [
    scale(add(corners[0], corners[1]), 0.5),
    scale(add(corners[1], corners[2]), 0.5),
    scale(add(corners[2], corners[0]), 0.5),
  ];
  const samples = [...corners, ...mids, center];
  const colors = samples.map(classify);
  const longest = Math.max(
    length(sub(corners[0], corners[1])), length(sub(corners[1], corners[2])), length(sub(corners[2], corners[0])),
  );
  const nearest = Math.min(...samples.map((point) => distanceToPolyline(point, chain.smoothedPoints, chain.closed).distance));
  if (colors.every((color) => color === colors[0]) && nearest > 0.75 * longest) return leafFor(colors[0], inheritedExtruder);
  if (longest <= tolerance || depth >= 10) return leafFor(majority(colors), inheritedExtruder);
  const [a, b, c] = corners; const [m01, m12, m20] = mids;
  const children: PaintNode[] = [
    adaptiveTree([a, m01, m20], classify, chain, tolerance, inheritedExtruder, depth + 1),
    adaptiveTree([m01, b, m12], classify, chain, tolerance, inheritedExtruder, depth + 1),
    adaptiveTree([m12, c, m20], classify, chain, tolerance, inheritedExtruder, depth + 1),
    adaptiveTree([m01, m12, m20], classify, chain, tolerance, inheritedExtruder, depth + 1),
  ];
  if (children.every((child) => !child.children && child.state === children[0].state)) return children[0];
  return { splitSides: 3, specialSide: 0, state: 0, children };
}

function leafFor(extruder: number, inherited: number): PaintNode {
  return { splitSides: 0, specialSide: 0, state: stateForExtruder(extruder, inherited), children: null };
}

function majority(values: number[]): number {
  const counts = new Map<number, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
}

export function applyCommand(document: EditorDocument, command: EditCommand, direction: 'undo' | 'redo'): void {
  if (command.paintChanges) {
    for (const change of command.paintChanges) {
      const part = document.parts[change.partIndex]; const leaf = part.leaves[change.leafId];
      const extruder = direction === 'undo' ? change.oldExtruder : change.newExtruder;
      if (!leaf) continue;
      leaf.extruder = extruder;
      const node = nodeAtPath(part.triangles[leaf.sourceTriangle].tree, leaf.path);
      if (node) node.state = stateForExtruder(extruder, part.defaultExtruder);
    }
  }
  if (command.treeChanges) {
    const parts = new Set<number>();
    for (const change of command.treeChanges) {
      document.parts[change.partIndex].triangles[change.triangleIndex].tree = clonePaintTree(direction === 'undo' ? change.oldTree : change.newTree);
      parts.add(change.partIndex);
    }
    for (const partIndex of parts) refreshPart(document, partIndex);
  }
  refreshBoundaries(document);
  document.dirty = true;
}

function refreshPart(document: EditorDocument, partIndex: number): void {
  rebuildPartLeaves(document.parts[partIndex], partIndex);
}

export function refreshBoundaries(document: EditorDocument): void {
  const result = buildBoundaries(document.parts);
  document.boundaries = result.chains; document.boundaryGroups = result.groups; document.boundarySegments = result.segments;
}

export function resolvedTreeLeafCount(tree: PaintNode): number {
  if (!tree.children) return 1;
  return tree.children.reduce((sum, child) => sum + resolvedTreeLeafCount(child), 0);
}

export function maxTreeDepth(tree: PaintNode): number {
  if (!tree.children) return 0;
  return 1 + Math.max(...tree.children.map(maxTreeDepth));
}

export function resolveTreeState(tree: PaintNode, inherited: number): number | null {
  if (!tree.children) return resolvedState(tree.state, inherited);
  const states = tree.children.map((child) => resolveTreeState(child, inherited));
  return states.every((state) => state === states[0]) ? states[0] : null;
}
